// frontend/src/components/production-sequence/SequenceHeader.tsx
import { useTranslation } from 'react-i18next';
import { RecalcBadge } from './RecalcBadge';
import type { UseProductionSequenceReturn } from '@/hooks/useProductionSequence';

export function SequenceHeader({ seq, dark }: {
  seq: UseProductionSequenceReturn; dark: boolean;
}) {
  const { t } = useTranslation();
  const card = dark ? 'bg-slate-900 border-white/8' : 'bg-white border-slate-200';

  function Kpi({ label, value }: { label: string; value: number }) {
    return (
      <div className={`rounded-xl border px-4 py-3 ${card}`}>
        <div className={`text-[10px] font-bold uppercase tracking-wider ${dark ? 'text-slate-500' : 'text-slate-400'}`}>{label}</div>
        <div className={`text-2xl font-bold tabular-nums ${dark ? 'text-slate-100' : 'text-slate-800'}`}>{value.toLocaleString('es-ES')}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Fila 1: título (las acciones —ordenar, configurar, exportar— viven en la barra de herramientas fija) */}
      <div>
        <h1 className={`text-lg font-bold ${dark ? 'text-slate-100' : 'text-slate-800'}`}>{t(seq.sequenceMode === 'fabricacion' ? 'sequence.titleFabricacion' : 'sequence.title')}</h1>
        <p className={`text-[12px] ${dark ? 'text-slate-400' : 'text-slate-500'}`}>{t('sequence.subtitle')}</p>
      </div>

      {/* Fila 2: KPIs */}
      <div className="grid grid-cols-2 gap-3 sm:max-w-md">
        <Kpi label={t('sequence.kpi.udComponentesOk')} value={seq.kpis.totalUdComponentesOK} />
        <Kpi label={t('sequence.kpi.udGranelOk')} value={seq.kpis.totalUdGranelOK} />
      </div>

      {/* Fila 3: badge vista personalizada */}
      {seq.isCustomOrder && (
        <div><RecalcBadge dark={dark} onReset={seq.resetToAutoOrder} /></div>
      )}

      {/* Fila 4: badge de recálculo de fechas */}
      {seq.isRecalcPending && seq.lastRecalcResults.length > 0 && (
        <div className={`inline-flex flex-wrap items-center gap-2 px-2.5 py-1 rounded-lg text-[11px] font-semibold ${
          dark ? 'bg-sky-950/40 text-sky-300 border border-sky-500/30' : 'bg-sky-50 text-sky-700 border border-sky-200'
        }`}>
          <span>
            {t('sequence.recalc.badge', {
              count: seq.lastRecalcResults.length,
              ot: seq.lastRecalcResults[0].orderId,
            })}
          </span>
          <button
            onClick={seq.resetRecalc}
            className={`underline underline-offset-2 cursor-pointer ${dark ? 'hover:text-sky-100' : 'hover:text-sky-900'}`}
          >
            {t('sequence.recalc.reset')}
          </button>
          <button
            type="button"
            disabled
            title={t('sequence.recalc.saveTooltip')}
            className="px-2 py-0.5 rounded bg-slate-300/40 text-slate-500 cursor-not-allowed"
          >
            💾 {t('sequence.recalc.save')}
          </button>
        </div>
      )}
    </div>
  );
}
